// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext, HasCartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import CartLineCardExpanded from "@insite/content-library/Widgets/Cart/CartLineCardExpanded";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@plumberssupply/App/Components/Notify";
import XngPostWarehouseTransfer from "@plumberssupply/App/Handlers/XNG/WarehouseTransfer";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getSettingsCollection } from "@plumberssupply/App/Store/Selectors/SettingsSelectors";
import Button, { ButtonPresentationProps } from "@plumberssupply/MobiusOverrides/Button/Button";
import DataTable, { DataTableProps } from "@plumberssupply/MobiusOverrides/DataTable/DataTable";
import DataTableHeader, { DataTableHeaderProps } from "@plumberssupply/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@plumberssupply/MobiusOverrides/DataTable/DataTableRow";
import { color, ellipsis, xScreen } from "@plumberssupply/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState & ApplicationState) => ({
    warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
    currentCart: getCurrentCartState(state).value,
    isCartLoading: getCurrentCartState(state).isLoading,
    settingsCollection: getSettingsCollection(state),
    promotionsDataView: getCurrentPromotionsDataView(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
});

const mapDispatchToProps = {
    loadWarehouses,
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
    XngPostWarehouseTransfer,
    loadRealTimeInventory,
};

type Props = HasCartLineContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WarehouseTransferStyles {
    container?: InjectableCss;
    select?: SelectPresentationProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    actionDataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: DataTableCellProps;
    dataCellQuantity?: DataTableCellProps;
    transferButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
    messageText?: TypographyPresentationProps;
    loadingWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    descriptipnCell?: DataTableCellProps;
    partNumbersStyles?: ProductPartNumbersStyles;
}

export const WarehouseTransferStyles: WarehouseTransferStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            > div {
                width: 60%;
            }
            ${xScreen("lte", "md")} {
                > div {
                    width: 100%;
                }
            }
        `,
    },
    select: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    dataTableBody: {
        css: css`
            tr:nth-child(even):hover .payInvoiceCell.payInvoiceCell.payInvoiceCell.payInvoiceCell {
                background-color: ${color("common.ui12")};
            }
        `,
    },
    dataTableRow: {
        css: css`
            &:hover {
                background: none;
                td:not(.payInvoiceCell) {
                    background-color: ${color("common.ui1", 0.15)};
                }
            }
        `,
    },
    dataCell: {
        css: css`
            font-size: 14px;
            text-align: center;
        `,
    },
    dataCellQuantity: {
        css: css`
            font-size: 14px;
            text-align: center;
            > div {
                margin: 0;
                width: auto;
            }
        `,
    },
    dataTableHeaders: {
        css: css`
            text-align: center;
        `,
    },
    transferButton: {
        css: css`
            margin-bottom: 20px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            text-align: end;
        `,
    },
    messageText: {
        size: 12,
        weight: "bold",
        css: css`
            margin-left: 5px;
        `,
    },
    loadingWrapper: {
        css: css`
            text-align: center;
        `,
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: 10,
                weight: "normal",
                css: css`
                    line-height: 8px;
                    color: ${color("text")};
                `,
            },
            css: css`
                margin-bottom: 6px;
                display: block;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    ${ellipsis(2)}
                `,
            },
            css: css`
                display: block;
                min-height: unset;
                margin-bottom: 10px;
                min-width: 200px;
            `,
        },
    },
    descriptipnCell: {
        css: css`
            white-space: normal;
            max-width: 200px;
        `,
    },
    dataTable: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    partNumbersStyles: {
        container: {
            offsetCss: css`
                &&& {
                    flex-direction: row;
                    flex-wrap: nowrap;
                }
            `,
        },
    },
};

const styles = WarehouseTransferStyles;

const WarehouseTransfer: FC<Props> = ({
    warehousesDataView,
    loadWarehouses,
    currentCart,
    updateCartLine,
    removeCartLine,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    isUpdatingCartLine,
    isCartLoading,
    XngPostWarehouseTransfer,
    loadRealTimeInventory,
}) => {
    const [transferTo, setTransferTo] = useState("");
    const [transferFrom, setTransferFrom] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [transferNumMessage, setTransferNumMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [cart, setCart] = useState<CartModel | undefined>();

    useEffect(() => {
        loadWarehouses({ search: "", latitude: 0, longitude: 0, pageSize: 999 });
    }, []);

    useEffect(() => {
        if (!currentCart || !currentCart.cartLines || currentCart.cartLines.length === 0) {
            return;
        }

        loadRealTimeInventory({
            productIds: currentCart?.cartLines.map(cartLine => cartLine.productId || ""),
            onComplete: realTimeInventoryProps => {
                if (!realTimeInventoryProps.error) {
                    const inventories = realTimeInventoryProps?.apiResult?.realTimeInventoryResults;
                    const wtCart = cloneDeep(currentCart);

                    wtCart.cartLines?.forEach(cartLine => {
                        const productInventory = inventories?.find(productInventory => productInventory.productId === cartLine.productId);
                        if (productInventory) {
                            cartLine.qtyOnHand = productInventory.qtyOnHand;
                            const inventoryAvailability = productInventory?.inventoryWarehousesDtos?.find(o => o.unitOfMeasure === cartLine.unitOfMeasure);
                            if (inventoryAvailability) {
                                (cartLine as any).warehouseDtos = inventoryAvailability.warehouseDtos;
                            }
                            if(transferFrom){
                                cartLine.qtyOnHand = (cartLine as any).warehouseDtos.find((warehouseDto: any) => transferFrom.toLowerCase() === warehouseDto.name.toLowerCase())?.qty;
                            }
                        }
                    });

                    setCart(wtCart);
                }
            },
        });
    }, [currentCart?.cartLines]);

    const transferFromHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTransferFrom(event.target.value);
        updateQtyOnHand(event.target.value);
    };

    const updateQtyOnHand = (warehouse?: string) => {
        if (warehouse) {
            const wtCart = cloneDeep(cart);
            wtCart?.cartLines?.forEach((cartLine) => {
                cartLine.qtyOnHand = (cartLine as any).warehouseDtos.find((warehouseDto: any) => warehouse.toLowerCase() === warehouseDto.name.toLowerCase())?.qty;
            });
            setCart(wtCart);
        }
    }

    const transferToHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTransferTo(event.target.value);
    };

    const onSubmitHandler = () => {
        setIsLoading(true);

        const itemsToTransfer = cart?.cartLines?.map(cartLine => {
            return {
                ProductCode: cartLine.erpNumber,
                QuantityOrdered: cartLine.qtyOrdered,
            };
        });

        const selectedWarehouses = [
            {
                ToWarehouse: transferTo,
                Warehouse: transferFrom,
            },
        ];

        if (!itemsToTransfer || !selectedWarehouses) {
            setIsLoading(false);
            return;
        }

        XngPostWarehouseTransfer({
            Inheader: [...selectedWarehouses],
            Inlines: itemsToTransfer,
            onSuccess: apiResult => {
                setIsLoading(false);
                setErrorMessage("");
                setTransferNumMessage(apiResult.transferNumber);
                clearCurrentCart();
            },
            onError: error => {
                setIsLoading(false);
                setErrorMessage(error);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    this.onSuccess?.(resultProps.apiResult);
                } else if (resultProps.apiResult?.errorMessage) {
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const { value: promotions } = promotionsDataView;
    const { productSettings } = settingsCollection;

    return (
        <StyledWrapper {...styles.container}>
            <Select {...styles.select} onChange={transferFromHandler}>
                <option selected value="">
                    {translate("Select A Warehouse To Transfer From")}
                </option>
                {warehousesDataView?.value?.map(warehouse => (
                    <option key={warehouse.name} value={warehouse.name}>
                        {warehouse.name}
                    </option>
                ))}
            </Select>
            <Select {...styles.select} onChange={transferToHandler}>
                <option selected value="">
                    {translate("Select A Warehouse To Transfer To")}
                </option>
                {warehousesDataView?.value?.map(warehouse => (
                    <option key={warehouse.name} value={warehouse.name}>
                        {warehouse.name}
                    </option>
                ))}
            </Select>

            {transferTo !== "" && transferFrom !== "" && transferTo === transferFrom && (
                <Notify type="warning">
                    {siteMessage("Warehouse_SimilarWareHouses", "To warehouse and from warehouse should be different")}
                </Notify>
            )}
            {!transferNumMessage && cart?.lineCount === 0 && (
                <Notify type="info">{siteMessage("Warehouse_Empty_Cart", "There are no items in your cart.")}</Notify>
            )}

            {transferNumMessage && (
                <Notify type="success">
                    {translate(`Transfer is done successfully`)}
                    <Typography {...styles.messageText}>
                        {translate(`Transfer Number : ${transferNumMessage}`)}
                    </Typography>
                </Notify>
            )}
            {!transferNumMessage && cart?.cartLines &&
                <StyledWrapper>
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button
                            {...styles.transferButton}
                            onClick={onSubmitHandler}
                            disabled={
                                !transferTo ||
                                !transferFrom ||
                                transferTo === transferFrom ||
                                cart?.lineCount === 0 ||
                                isUpdatingCartLine ||
                                isLoading
                            }
                        >
                            {translate("Transfer Items")}
                        </Button>
                    </StyledWrapper>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("")}</DataTableHeader>
                            <DataTableHeader>{translate("Product")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>
                                {translate("QTY Available")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>
                                {translate("QTY Ordered")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>
                                {translate("QTY Shipped")}
                            </DataTableHeader>
                        </DataTableHead>

                        <DataTableBody {...styles.dataTableBody}>
                            {cart?.cartLines?.map((cartline, index) => {
                                const matchingPromotions = promotions
                                    ? promotions.filter(promo => promo.orderLineId === cartline.id)
                                    : [];
                                return (
                                    <DataTableRow key={cartline.id} {...styles.dataTableRow}>
                                        <DataTableCell {...styles.dataCell}> {index + 1}</DataTableCell>
                                        <DataTableCell {...styles.descriptipnCell}>
                                            {cartline.brand && (
                                                <ProductBrand
                                                    brand={cartline.brand}
                                                    extendedStyles={styles.productBrandStyles}
                                                />
                                            )}
                                            <ProductDescription
                                                product={cartline}
                                                extendedStyles={styles.productDescriptionStyles}
                                            />
                                            <ProductPartNumbers
                                                manufacturerItem={cartline.manufacturerItem}
                                                productNumber={cartline.erpNumber}
                                                extendedStyles={styles.partNumbersStyles}
                                            />
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCell}>{cartline.qtyOnHand}</DataTableCell>
                                        <DataTableCell {...styles.dataCellQuantity}>
                                            <CartLineContext.Provider value={cartline} key={cartline.id}>
                                                <CartLineCardExpanded
                                                    cart={cart}
                                                    promotions={matchingPromotions}
                                                    productSettings={productSettings}
                                                    updateCartLine={updateCartLine}
                                                    removeCartLine={removeCartLine}
                                                    hidePriceForEmployee={true}
                                                    {...({ warehouseTransfer: true } as any)}
                                                />
                                            </CartLineContext.Provider>
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCell}>
                                            {cartline.qtyOrdered !== null &&
                                                (cartline.qtyOnHand > 0 && cartline.qtyOnHand >= cartline.qtyOrdered
                                                    ? cartline.qtyOrdered
                                                    : (cartline.qtyOnHand > 0 && cartline.qtyOnHand <= cartline.qtyOrdered)
                                                        ? cartline.qtyOrdered - cartline.qtyOnHand < 1
                                                            ? 1
                                                            : cartline.qtyOnHand
                                                        : cartline.qtyOnHand)
                                            }
                                        </DataTableCell>
                                    </DataTableRow>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button
                            {...styles.transferButton}
                            onClick={onSubmitHandler}
                            disabled={
                                !transferTo ||
                                !transferFrom ||
                                transferTo === transferFrom ||
                                cart?.lineCount === 0 ||
                                isUpdatingCartLine ||
                                isLoading
                            }
                        >
                            {translate("Transfer Items")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            }

            {errorMessage && (
                <Notify type="danger">
                    {siteMessage("WarehouseTransfer_Error", "An error happens while tranfer")}
                </Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WarehouseTransfer),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Warehouse Transfer",
        fieldDefinitions: [],
    },
};

export default widgetModule;
